import { BehaviorSubject } from 'rxjs/BehaviorSubject';

import {
	ConvoAIResponseState,
	type ConvoAIStreamingState,
	getDefaultConvoAIStreamingState,
} from '../../models/ConvoAIStreamingState';
import { CONVOAI_DEFAULT_ERROR_CODE, ConvoAIErrorMessage } from './ConvoAIStreamingModels';
import { type ConvoAIRequestConfig } from './createConvoAIRequestConfig';
import { readStream } from './readConvoAIStream';

export function makeConvoAIStreamRequest(
	requestConfig: ConvoAIRequestConfig,
	handleTransportErrors = false,
): BehaviorSubject<ConvoAIStreamingState> {
	const behaviorSubject = new BehaviorSubject<ConvoAIStreamingState>(
		getDefaultConvoAIStreamingState(),
	);

	const requestPromise = fetch(requestConfig.url, {
		credentials: 'include',
		...requestConfig.requestInitParams,
	})
		.then((response) => readStream(response))
		.then(async (stream) => {
			let bufferContent = '';
			let answerChunkIndex = 0;
			for await (const chunk of stream) {
				const state = behaviorSubject.getValue();

				switch (chunk.type) {
					case 'ANSWER_PART':
						bufferContent += chunk.message.content;
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.AnswerPart,
							content: bufferContent,
							answerChunkIndex,
						});
						answerChunkIndex += 1;
						break;
					case 'CHANNEL_ID':
						const channelId = chunk.message.content;
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.ChannelId,
							content: channelId,
							answerChunkIndex: undefined,
						});
						break;
					case 'FINAL_RESPONSE':
						bufferContent = chunk.message.content;
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.FinalResponse,
							content: bufferContent,
							answerChunkIndex: undefined,
						});
						break;
					case 'FOLLOW_UP_QUERIES':
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.FollowUp,
							answerChunkIndex: undefined,
							followUpContent: chunk.message.follow_ups ?? [],
						});
						break;
					case 'TRACE':
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.Trace,
							traceContent: chunk.message?.content ?? null,
							answerChunkIndex: undefined,
						});
						break;
					case 'ERROR': {
						if (handleTransportErrors && state.responseState === ConvoAIResponseState.Error) {
							break;
						}
						behaviorSubject.next({
							...state,
							responseState: ConvoAIResponseState.Error,
							answerChunkIndex: undefined,
							error: { ...chunk },
						});
						break;
					}
				}
			}
			if (
				handleTransportErrors &&
				behaviorSubject.getValue().responseState === ConvoAIResponseState.Initialization
			) {
				emitUnexpectedTransportError('The stream completed without a response.');
			}
		});

	if (handleTransportErrors) {
		requestPromise
			.catch((error: unknown) => {
				if (behaviorSubject.getValue().responseState === ConvoAIResponseState.Error) {
					return;
				}
				const message = error instanceof Error ? error.message : String(error);
				emitUnexpectedTransportError(message || 'An unexpected streaming error occurred.');
			})
			.finally(() => {
				behaviorSubject.complete();
			});
	} else {
		requestPromise.finally(() => {
			behaviorSubject.complete();
		});
	}

	function emitUnexpectedTransportError(content: string) {
		behaviorSubject.next({
			content: null,
			responseState: ConvoAIResponseState.Error,
			error: {
				type: 'ERROR',
				message: {
					content,
					message_template: ConvoAIErrorMessage.UNEXPECTED,
					status_code: CONVOAI_DEFAULT_ERROR_CODE,
				},
			},
		});
	}

	return behaviorSubject;
}
